import React, { useEffect, useState } from "react";
import {
  Box,
  CardContent,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Stack,
} from "@mui/material";
import ViewCarouselOutlinedIcon from "@mui/icons-material/ViewCarouselOutlined";
import SlideshowOutlinedIcon from "@mui/icons-material/SlideshowOutlined";
import { useDispatch, useSelector } from "react-redux";
import CarouselTable from "./CarouselTable";
import CarouselManager from "./create-carousel/CarouselManager";
import {
  fetchCarouselRequest,
  addCarouselLocationRequest,
} from "../../../redux/slices/CarouselSlice";
import { canShowPermission } from "../Permissions/permissionObject";
import { toast } from "react-toastify";
import {
  carouselGradientBtn,
  carouselSectionCard,
  carouselSectionHeader,
} from "./carouselStyles";

const Carousel = () => {
  const dispatch = useDispatch();
  const { data: carouselState, locationActionLoading, loading } = useSelector(
    (state) => state.carousel
  );
  const [screen, setScreen] = useState("home");
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [locationName, setLocationName] = useState("");
  const [permissions, setPermissions] = useState({});

  useEffect(() => {
    dispatch(fetchCarouselRequest());
  }, [dispatch]);

  useEffect(() => {
    const storedPermissions = localStorage.getItem("userPermissions");
    if (storedPermissions) {
      try {
        setPermissions(JSON.parse(storedPermissions));
      } catch (error) {
        console.error("Error parsing stored permissions", error);
      }
    }
  }, []);

  const canAddLocation =
    canShowPermission(permissions, "carouselsettings", "create") ||
    canShowPermission(permissions, "carouselsettings", "edit");

  const filteredCount = (carouselState?.carousellist || []).filter((c) =>
    c?.location?.startsWith(screen)
  ).length;

  const handleAddLocation = () => {
    if (!screen) {
      toast.error("Please select a screen first.");
      return;
    }
    setAddDialogOpen(true);
  };

  const handleConfirmAddLocation = () => {
    const label = locationName.trim();
    if (!label) {
      toast.error("Please enter a location name.");
      return;
    }
    if (/\s/.test(label)) {
      toast.error("Location name cannot contain spaces.");
      return;
    }
    dispatch(addCarouselLocationRequest({ screen, label }));
    setAddDialogOpen(false);
    setLocationName("");
  };

  const screenSelector = (
    <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: 160 } }}>
      <InputLabel>Screen</InputLabel>
      <Select
        value={screen}
        label="Screen"
        onChange={(e) => setScreen(e.target.value)}
      >
        {(carouselState?.carouselscreens || ["home"]).map((item) => (
          <MenuItem key={item} value={item}>
            {item}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  return (
    <Box sx={{ width: "100%", pb: 2 }}>
      {/* Carousel List */}
      <Box sx={{ ...carouselSectionCard, mb: 3 }}>
        <Box sx={carouselSectionHeader}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <ViewCarouselOutlinedIcon sx={{ color: "#7A3FFF", fontSize: 28 }} />
            <Box>
              <Typography fontWeight={700} fontSize={{ xs: 18, sm: 22 }}>
                Carousel List
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Configure height, status & duration per location
              </Typography>
            </Box>
            {screen && (
              <Chip
                label={`${filteredCount} location${filteredCount !== 1 ? "s" : ""}`}
                size="small"
                sx={{ bgcolor: "#f2eaff", color: "#7A3FFF", fontWeight: 600 }}
              />
            )}
          </Stack>
          {canAddLocation && (
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1.5}
              sx={{ width: { xs: "100%", sm: "auto" } }}
            >
              {screenSelector}
              <Button
                variant="contained"
                fullWidth
                disabled={locationActionLoading || !screen}
                onClick={handleAddLocation}
                sx={carouselGradientBtn}
              >
                Add Location
              </Button>
            </Stack>
          )}
        </Box>
        <CardContent sx={{ p: { xs: 1.5, sm: 2, md: 3 }, pt: { xs: 2, sm: 2 } }}>
          <CarouselTable screenFilter={screen} loading={loading} />
        </CardContent>
      </Box>

      {/* Carousel Slides */}
      <Box sx={carouselSectionCard}>
        <Box sx={carouselSectionHeader}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <SlideshowOutlinedIcon sx={{ color: "#7A3FFF", fontSize: 28 }} />
            <Box>
              <Typography fontWeight={700} fontSize={{ xs: 18, sm: 22 }}>
                Carousel Slides
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Manage slides for each carousel location
              </Typography>
            </Box>
          </Stack>
          <Box sx={{ width: { xs: "100%", sm: 280 } }}>{screenSelector}</Box>
        </Box>
        <CardContent sx={{ p: { xs: 1.5, sm: 2, md: 3 }, pt: { xs: 2, sm: 2 } }}>
          <CarouselManager selectedScreen={screen} />
        </CardContent>
      </Box>

      <Dialog
        open={addDialogOpen}
        onClose={() => {
          setAddDialogOpen(false);
          setLocationName("");
        }}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Add carousel location</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label="Location name"
            placeholder="festive_offers"
            value={locationName}
            onChange={(e) =>
              setLocationName(e.target.value.replace(/\s/g, ""))
            }
            onKeyDown={(e) => {
              if (e.key === " ") e.preventDefault();
              if (e.key === "Enter") handleConfirmAddLocation();
            }}
            helperText={`No spaces. Added under screen: ${screen}`}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => {
              setAddDialogOpen(false);
              setLocationName("");
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={!locationName.trim() || locationActionLoading}
            onClick={handleConfirmAddLocation}
            sx={carouselGradientBtn}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Carousel;
